import CompetencyRating from 'components/general/competencyRating';
import {
    ArrayHelpers,
    Copy,
    DeleteWarning,
    DomHelpers,
    Forms,
    Help,
    SaveButton,
    SystemText,
} from '@salesperformanceint/e4shared';
import { hasModule } from 'helpers/profile';
import React from 'react';
import { useSelector } from 'react-redux';
import { e4Format } from 'helpers/dates';
import { withRouter } from 'react-router-dom';
import PositiveIndicators from 'components/user/positiveIndicators';
import Subheader from 'components/prism/subheader';
import Rating from 'components/prism/rating';
import PISummary from '../piSummary';
import { PDF } from '../../../pdf';

export const Summary = props => {
    const {
        profile,
        pages,
        comments,
        state,
        setComments,
        predictions,
        appraisal,
        setInReview,
        setPage,
        setStartPage,
        setEndPage,
        finalise,
        setAgreedRatingValue,
        goals,
        usePrismSummary,
        error,
        history,
        match,
    } = props;

    const { tags, handles } = useSelector(state => state.ui);

    const [showWarning, setShowWarning] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [finalError, setFinalError] = React.useState(false);
    const [ratings, setRatings] = React.useState([]);
    const [seeMore, setSeeMore] = React.useState(null);

    const user = useSelector(state => state.user.user);
    const guid = match.params.guid;

    const prismSuffix = user.companySettings?.prism_UI ? '/Prism' : '';

    const hideColourFromUser = user.companySettings?.development?.greyRatingsForUserReviews;
    const json = JSON.parse(appraisal?.json || '{}');

    const loaded = React.useRef(null);

    React.useEffect(() => {
        const temp = pages.reduce((a, c, i) => {
            const existing = a.find(x => x.id === c.id);
            if (existing) {
                existing.pageEnd = i + 1;
                return a;
            } else {
                return a.concat([{ ...c, pageStart: i + 1, pageEnd: i + 1 }]);
            }
        }, []);

        setRatings(temp);
    }, [pages]);

    const finalValidate = () => {
        let err = '';
        if (!comments) {
            err = SystemText('Reviews/Appraisals/Wizard/Errors/InProgress');
        }

        const values = JSON.parse(appraisal.ratings || '[]');

        ratings.forEach((r, i) => {
            const level = values.find(x => x.id === r.id)?.level;
            if (!level) {
                err = SystemText('Reviews/Appraisals/Wizard/Errors/InProgress');
            }
        });

        setFinalError(err);

        err && setSaving(false);

        return !err;
    };

    const positiveIndicatorDetail = (appraisal, r) => {
        if (!ArrayHelpers.existsAndHasLength(r.positiveIndicators)) return null;
        const managerPredictions = JSON.parse(appraisal.manager_Predictions || '[]');
        const userPredictions = JSON.parse(appraisal.user_Predictions || '[]');
        const managerPrediction = managerPredictions?.find(x => x.competency_Id === r.id);
        const userPrediction = userPredictions?.find(x => x.competency_Id === r.id);

        r.positiveIndicators.map((pi, i) => {
            pi.userRated = 0;
            pi.managerRated = 0;

            if (userPrediction?.positiveIndicators[i + 1]) {
                pi.userRated = userPrediction.positiveIndicators[i + 1];
            }

            if (managerPrediction?.positiveIndicators[i + 1]) {
                pi.managerRated = managerPrediction.positiveIndicators[i + 1];
            }
        });

        return (
            <span className="hidden-sm">
                <Help id={`help-pis-${r.id}`} target={<i className="ml10 fa fa-list" />}>
                    <div className="mb10">{SystemText('Reviews/Appraisals/Wizard/PositiveIndicatorsIntro')}</div>
                    <PositiveIndicators
                        positiveIndicators={r.positiveIndicators}
                        hideColour={state.user && hideColourFromUser}
                    />
                </Help>
            </span>
        );
    };

    const positiveIndicatorScore = (appraisal, r, who) => {
        const managerPredictions = JSON.parse(appraisal.manager_Predictions || '[]');
        const userPredictions = JSON.parse(appraisal.user_Predictions || '[]');

        const prediction =
            who == 'M'
                ? managerPredictions?.find(x => x.competency_Id === r.id)
                : userPredictions?.find(x => x.competency_Id === r.id);

        if (prediction?.level) return prediction.level;

        const rating = r.positiveIndicators.reduce((a, pi, i) => {
            let value = who === 'M' ? pi.managerRated : pi.userRated;
            value =
                value || prediction
                    ? prediction?.positiveIndicators?.length >= i && prediction?.positiveIndicators[i + 1]
                    : 0;
            return a + value;
        }, 0);

        return Math.round(rating / r.positiveIndicators.length, 0);
    };

    const prepareAndFinalise = confirm => {
        if (confirm) {
            appraisal.comments = comments;
            finalise(appraisal, () => {
                setSaving(false);
            });
        } else {
            setSaving(false);
        }

        setShowWarning(false);
    };

    const performanceDashButton = () => {
        if (user.licence !== 'FULL') return null;
        const allowReset = !user.samlOnly && !user.hasPassword;

        if (window.location.href.toLowerCase().indexOf('develop/reviews') >= 0) return null;

        const link = state.user
            ? allowReset
                ? `/reset-password?email=${encodeURIComponent(user.email)}&guid=${guid}`
                : '/develop/performance-dashboard'
            : `/develop/reviews/${profile.id}`;
        const text = state.user
            ? allowReset
                ? SystemText('Reviews/Appraisals/Wizard/Congratulations/Password/Button')
                : SystemText(`Reviews/Appraisals/Wizard/Congratulations/User/Button${prismSuffix}`, profile.first_Name)
            : SystemText('Reviews/Appraisals/Wizard/Congratulations/Manager/Button', profile.first_Name);
        return state.completed && !usePrismSummary ? (
            <div className="mt10 pull-right">
                <button className="btn btn-primary" onClick={() => history.push(link)}>
                    {text}
                </button>
            </div>
        ) : null;
    };

    React.useEffect(() => {
        loaded.current && setTimeout(DomHelpers.matchWidth('match-width', loaded.current), 250);
    }, [loaded.current]);

    const pdfButton = appraisal ? <PDF key="appraisal-buttons-pdf" appraisal={appraisal} prism={usePrismSummary} /> : null;

    const isPrism = user.companySettings?.prism_UI;

    const showComments = appraisal.participants === 'UM' && (isPrism || user.reviewViaSitdown);

    const commentLabelCssClass = isPrism ? 'bold' : '';

    const managersCommentLabel = isPrism
        ? SystemText('Reviews/Appraisals/TeamMember/ManagerComments')
        : SystemText('Reviews/Appraisals/ManagerComments');
    const usersCommentLabel = isPrism
        ? SystemText('Reviews/Appraisals/TeamMember/UserComments', profile.first_Name)
        : SystemText('Reviews/Appraisals/UserComments');
    const finalCommentLabel = SystemText(
        `Reviews/Appraisals/Wizard/Fields/${state.inProgress || state.past ? 'FinalComments' : 'Comments'}/Label`
    );

    const individualCommentSection = showComments && (state.inProgress || state.past) && (
        <div className="row pb10">
            <div className={`col-md-6 ${commentLabelCssClass}`}>
                <Forms.Label label={managersCommentLabel} value={appraisal.manager_Comments} />
            </div>
            <div className={`col-md-6 ${commentLabelCssClass}`}>
                <Forms.Label label={usersCommentLabel} value={appraisal.user_Comments} />
            </div>
        </div>
    );
    const finalCommentSection = showComments && (
        <div className="text-left hide-error-text">
            {state.past ? (
                <div className={commentLabelCssClass}>
                    <Forms.Label label={finalCommentLabel} value={appraisal.comments} />
                </div>
            ) : (
                <Forms.Textarea
                    error={(error || finalError) && !comments}
                    id="competency-comments"
                    label={SystemText(
                        `Reviews/Appraisals/Wizard/Fields/${state.inProgress ? 'FinalComments' : 'Comments'}/Label`
                    )}
                    rows="5"
                    readonly={state.readonly && !state.inProgress}
                    tags={tags}
                    handles={handles}
                    mandatory
                    placeholder={SystemText(
                        `Reviews/Appraisals/Wizard/Fields/${state.inProgress ? 'FinalComments' : 'Comments'}/Placeholder`
                    )}
                    value={comments}
                    onChange={setComments}
                />
            )}
        </div>
    );

    return (
        <div className="mb20" ref={loaded}>
            {usePrismSummary ? (
                <Subheader title={SystemText('Reviews/Summary/Title')} extra={appraisal.submission && pdfButton} />
            ) : (
                <>
                    <div className="uppercase">
                        {SystemText('Reviews/Appraisals/Wizard/Scheduled')} {e4Format(appraisal.scheduled)}
                    </div>
                    <h2>
                        {state.inProgress
                            ? SystemText(
                                  'Reviews/Appraisals/Wizard/Headings/H2/ReviewSummary/Title/InProgress',
                                  profile.first_Name
                              )
                            : state.user
                              ? SystemText('Reviews/Appraisals/Wizard/Headings/H2/ReviewSummary/Title/User')
                              : `${profile.first_Name}${SystemText(
                                    'Reviews/Appraisals/Wizard/Headings/H2/ReviewSummary/Title/Profile'
                                )}`}
                    </h2>
                </>
            )}
            {state.readonly ? (
                state.inProgress ? (
                    <Copy value={SystemText('Reviews/Appraisals/Wizard/Paragraphs/Ratings/InProgress/1')} />
                ) : state.past ? (
                    <Copy
                        value={SystemText(
                            `Reviews/Appraisals/Wizard/Paragraphs/Ratings/Past${state.wizard ? '/Wizard' : ''}${
                                user.companySettings?.development?.usePerformanceDashboard ? '/PerformanceDashboard' : ''
                            }`
                        )}
                    />
                ) : (
                    <p>{SystemText('Reviews/Appraisals/Wizard/Paragraphs/Ratings/PastReview/1')}</p>
                )
            ) : (
                <>
                    <p>{SystemText('Reviews/Appraisals/Wizard/Paragraphs/Ratings/Review/1')}</p>
                    <p>{SystemText('Reviews/Appraisals/Wizard/Paragraphs/Ratings/Review/2')}</p>
                    <p>
                        {SystemText(
                            `Reviews/Appraisals/Wizard/Paragraphs/Ratings/Review/3${
                                appraisal.participants !== 'UM' ? '/Zero' : ''
                            }`
                        )}
                    </p>
                    <div id="error-anchor" />
                    {goals && Object.keys(goals).length > 0 && hasModule(user, 'integrations') && (
                        <div className="mt10">
                            {SystemText('Reviews/Appraisals/Wizard/ReviewSummary/Goals/Intro')}{' '}
                            <button
                                className="btn btn-xs btn-info"
                                style={{ marginTop: '-3px' }}
                                onClick={() => {
                                    setInReview(true);
                                    setPage(0);
                                }}
                            >
                                {SystemText(
                                    `Reviews/Appraisals/Wizard/ReviewSummary/Goals/Link/${state.user ? 'You' : 'Them'}`
                                )}
                            </button>
                        </div>
                    )}
                </>
            )}
            <div>
                <div>
                    <div className="appraisal-summary">
                        <table className={usePrismSummary ? 'prism-table' : 'table no-border mb0 review-summary'}>
                            <tbody>
                                {usePrismSummary ? (
                                    <tr>
                                        <th>{SystemText('Reviews/Summary/Table/Behaviour')}</th>
                                        {(state.user || state.past || state.inProgress) &&
                                            appraisal.participants.indexOf('U') >= 0 && (
                                                <th>{SystemText('Reviews/Summary/Table/YourRating')}</th>
                                            )}
                                        {(state.manager || state.past || state.inProgress) &&
                                            appraisal.participants.indexOf('M') >= 0 && (
                                                <th>{SystemText('Reviews/Summary/Table/ManagerRating')}</th>
                                            )}
                                        {appraisal.participants === 'UM' &&
                                            appraisal.user_Comments_Done &&
                                            appraisal.manager_Comments_Done &&
                                            (state.past || state.inProgress) && (
                                                <th>{SystemText('Reviews/Summary/Table/AgreedRating')}</th>
                                            )}
                                        <th></th>
                                    </tr>
                                ) : (
                                    <>
                                        {(state.past || state.inProgress) && (
                                            <tr>
                                                {appraisal.participants.indexOf('M') >= 0 && (
                                                    <td className="p10 text-center" width="1">
                                                        <i
                                                            className="fa fa-user-tie-hair"
                                                            title={SystemText(
                                                                'Reviews/Appraisals/Wizard/ReviewSummary/Icons/Manager'
                                                            )}
                                                        />
                                                    </td>
                                                )}
                                                {appraisal.participants.indexOf('U') >= 0 && (
                                                    <td className="p10 text-center" width="1">
                                                        <i
                                                            className="fa fa-user"
                                                            title={SystemText(
                                                                'Reviews/Appraisals/Wizard/ReviewSummary/Icons/User'
                                                            )}
                                                        />
                                                    </td>
                                                )}
                                                {appraisal.participants === 'UM' && (
                                                    <td className="p10 text-center" width="1">
                                                        <i
                                                            className="fa fa-handshake"
                                                            title={SystemText(
                                                                'Reviews/Appraisals/Wizard/ReviewSummary/Icons/Agreed'
                                                            )}
                                                        />
                                                    </td>
                                                )}
                                                <td></td>
                                            </tr>
                                        )}
                                    </>
                                )}

                                {ratings.map((r, i) => {
                                    let managerLevel =
                                        state.manager || state.inProgress ? predictions[r.id]?.managerLevel : 0;

                                    let userLevel = state.user || state.inProgress ? predictions[r.id]?.userLevel : 0;

                                    if (state.manager && !state.past) {
                                        managerLevel = managerLevel || predictions[r.id]?.level;
                                    }

                                    if (state.user && !state.past) {
                                        userLevel = userLevel || predictions[r.id]?.level;
                                    }

                                    const comp = json?.competencies?.find(c => c.id === r.id);
                                    const values =
                                        json.ratings || JSON.parse(json.appraisal?.ratings || appraisal.ratings || '[]');

                                    const agreedLevel = values.some(x => x.id === r.id)
                                        ? values.find(x => x.id === r.id)?.level
                                        : 0;

                                    const agreedLevelFromComp = comp?.level;

                                    const managerCalculated = positiveIndicatorScore(appraisal, r, 'M');
                                    const userCalculated = positiveIndicatorScore(appraisal, r, 'U');

                                    if (!managerLevel && state.past && appraisal.participants.indexOf('M') >= 0) {
                                        managerLevel = comp?.manager || managerLevel || managerCalculated || agreedLevel;
                                    }

                                    if (!userLevel && state.past && appraisal.participants.indexOf('U') >= 0) {
                                        userLevel = comp?.staff || userLevel || userCalculated || agreedLevel;
                                    }

                                    if (predictions[r.id]) {
                                        if (appraisal?.user_Competency_Comments) {
                                            const userCommentsParsed = JSON.parse(appraisal.user_Competency_Comments);
                                            const userComment = userCommentsParsed.find(
                                                c => (c.id || c.competency_Id) === r.id
                                            );

                                            predictions[r.id].userComments = userComment
                                                ? userComment.comments
                                                : predictions[r.id].userComments;
                                        }

                                        if (appraisal?.manager_Competency_Comments) {
                                            const managerCommentsParsed = JSON.parse(appraisal.manager_Competency_Comments);
                                            const managerComment = managerCommentsParsed.find(
                                                c => (c.id || c.competency_Id) === r.id
                                            );

                                            predictions[r.id].managerComments = managerComment
                                                ? managerComment.comments
                                                : predictions[r.id].managerComments;
                                        }
                                    }

                                    if (usePrismSummary) {
                                        return (
                                            <tr key={`review-${r.id}`}>
                                                <td>{r.full_Name}</td>
                                                {(state.user || state.past || state.inProgress) &&
                                                    appraisal.participants.indexOf('U') >= 0 && (
                                                        <td className="p10 text-center" width="1" data-testid="rating-user">
                                                            <Rating
                                                                level={userLevel}
                                                                hideColour={state.user && hideColourFromUser}
                                                            />
                                                        </td>
                                                    )}
                                                {(state.manager || state.past || state.inProgress) &&
                                                    appraisal.participants.indexOf('M') >= 0 && (
                                                        <td
                                                            className="p10 text-center"
                                                            width="1"
                                                            data-testid="rating-manager"
                                                        >
                                                            <Rating
                                                                level={managerLevel}
                                                                hideColour={state.user && hideColourFromUser}
                                                            />
                                                        </td>
                                                    )}
                                                {appraisal.participants === 'UM' &&
                                                    appraisal.user_Comments_Done &&
                                                    appraisal.manager_Comments_Done &&
                                                    (state.past || state.inProgress) && (
                                                        <td
                                                            className="p10 text-center match-width"
                                                            width="1"
                                                            data-testid="rating-agreed"
                                                        >
                                                            <Rating
                                                                level={agreedLevel || agreedLevelFromComp}
                                                                hideColour={state.user && hideColourFromUser}
                                                            />
                                                        </td>
                                                    )}
                                                <td>
                                                    <button
                                                        className="btn btn-primary btn-cornered"
                                                        onClick={() => setSeeMore(r)}
                                                        disabled={(r.positiveIndicators || []).length === 0}
                                                    >
                                                        {SystemText('Reviews/Summary/Table/SeeMore')}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    return (
                                        <tr key={`review-${r.id}`}>
                                            {(state.manager || state.past || state.inProgress) &&
                                                appraisal.participants.indexOf('M') >= 0 && (
                                                    <td className="p10 text-center" width="1" data-testid="rating-manager">
                                                        <CompetencyRating
                                                            level={managerLevel}
                                                            badge
                                                            hideColour={state.user && hideColourFromUser}
                                                        />
                                                    </td>
                                                )}
                                            {(state.user || state.past || state.inProgress) &&
                                                appraisal.participants.indexOf('U') >= 0 && (
                                                    <td className="p10 text-center" width="1" data-testid="rating-user">
                                                        <CompetencyRating
                                                            level={userLevel}
                                                            badge
                                                            hideColour={state.user && hideColourFromUser}
                                                        />
                                                    </td>
                                                )}
                                            {appraisal.participants === 'UM' &&
                                                appraisal.user_Comments_Done &&
                                                appraisal.manager_Comments_Done &&
                                                (state.past || state.inProgress) && (
                                                    <td
                                                        className="p10 text-center match-width"
                                                        width="1"
                                                        data-testid="rating-agreed"
                                                    >
                                                        <CompetencyRating
                                                            level={agreedLevel || agreedLevelFromComp}
                                                            badge
                                                            hideColour={state.user && hideColourFromUser}
                                                            onChange={state.past ? null : x => setAgreedRatingValue(r.id, x)}
                                                        />
                                                    </td>
                                                )}
                                            <td className="p10 text-left">
                                                <div data-testid="button-summary-competency" className="">
                                                    <span className={finalError && !agreedLevel ? 'text-danger' : ''}>
                                                        {state.past ? (
                                                            <span>{r.full_Name}</span>
                                                        ) : (
                                                            <button
                                                                className="btn btn-link"
                                                                onClick={() => {
                                                                    setInReview(true);
                                                                    setPage(1);
                                                                    setStartPage(r.pageStart);
                                                                    setEndPage(r.pageEnd);
                                                                }}
                                                            >
                                                                {r.full_Name}
                                                            </button>
                                                        )}
                                                        {finalError && !agreedLevel && (
                                                            <i
                                                                className="ml10 fa fa-warning"
                                                                title={SystemText(
                                                                    'Reviews/Appraisals/Wizard/ReviewSummary/Agreed/Rate'
                                                                )}
                                                            />
                                                        )}
                                                    </span>
                                                    {(predictions[r.id]?.comments ||
                                                        predictions[r.id]?.managerComments ||
                                                        predictions[r.id]?.userComments) && (
                                                        <span className="hidden-sm">
                                                            <Help
                                                                id={`help-${r.id}`}
                                                                target={<i className="ml10 fa fa-comments" />}
                                                            >
                                                                {state.inProgress || state.past ? (
                                                                    <div style={{ width: '380px' }}>
                                                                        {appraisal.participants.indexOf('M') >= 0 && (
                                                                            <>
                                                                                <div className="comment-box text-center mb10">
                                                                                    <i className="fa fa-user-tie-hair mr10" />
                                                                                    <span>
                                                                                        {SystemText(
                                                                                            'Reviews/Appraisals/ManagerComments'
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    {predictions[r.id]?.managerComments}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                        {appraisal.participants.indexOf('U') >= 0 && (
                                                                            <>
                                                                                <div className="comment-box text-center mt10 mb10">
                                                                                    <i className="fa fa-user mr10" />
                                                                                    <span>
                                                                                        {SystemText(
                                                                                            'Reviews/Appraisals/UserComments'
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                                <div>{predictions[r.id]?.userComments}</div>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    predictions[r.id]?.comments
                                                                )}
                                                            </Help>
                                                        </span>
                                                    )}
                                                    {positiveIndicatorDetail(appraisal, r)}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {individualCommentSection}
                    {finalCommentSection}

                    {(error || finalError) && (
                        <div
                            id="error-anchor"
                            style={{ fontWeight: 500, fontSize: '14px' }}
                            className="mt10 p20 label label-danger flex flex-center"
                        >
                            <i className="fa fa-warning mr10" />
                            {error || finalError}
                        </div>
                    )}
                    {performanceDashButton()}
                    {state.inProgress && (
                        <SaveButton
                            saving={saving}
                            setSaving={setSaving}
                            className="btn btn-success"
                            onClick={() => finalValidate() && setShowWarning(true)}
                        >
                            <i className="fa fa-check mr10" />
                            {SystemText('Reviews/Appraisals/Buttons/Finalise')}
                        </SaveButton>
                    )}
                </div>
            </div>
            <DeleteWarning
                message={SystemText('Reviews/Appraisals/Buttons/Finalise/Warning')}
                show={showWarning}
                close={prepareAndFinalise}
            />
            <PISummary show={seeMore} close={() => setSeeMore(null)} />
        </div>
    );
};

export default withRouter(Summary);
